/**
 * Hooks del organigrama: el árbol, las unidades (división/área) y la asignación
 * organizacional de cada trabajador.
 *
 * ⚠️ El backend devuelve un **bosque** (`roots[]`), no un árbol: varias raíces es lo
 * normal —una empresa con dos gerencias tiene dos— y en un tenant recién migrado
 * pueden ser todas las personas. Nada acá asume una raíz única.
 */
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'

import apiClient from '@/lib/apiClient'

// ─── Types ──────────────────────────────────────────────────────────────────

export interface OrgNode {
  id: string
  name: string
  rut: string
  position: string
  area_id: string | null
  division_id: string | null
  children: OrgNode[]
  subordinate_count: number
  /** Directos + indirectos (lo que el rubro llama *total reports*). */
  team_size: number
  /** La persona está dentro de una jefatura circular: el dato hay que corregirlo. */
  cycle: boolean
}

export interface OrgPerson {
  id: string; name: string; rut: string; position: string
  area_id: string | null; division_id: string | null
}

export interface OrgCompleteness {
  total: number
  with_supervisor: number
  without_supervisor: number
  with_orphan_supervisor: number
  without_position: number
  with_unit: number
  in_cycle?: number
}

export interface SupervisorChart {
  by: 'supervisor'
  as_of: string | null
  roots: OrgNode[]
  /** Cuelgan de un jefe que ya no está en la empresa. */
  orphans: OrgPerson[]
  /** Jefaturas circulares (A→B→A): entran marcadas para que no desaparezcan. */
  in_cycle: OrgPerson[]
  completeness: OrgCompleteness
}

export interface UnitArea {
  id: string; name: string; code: string
  manager: string | null
  employees: OrgPerson[]
}

export interface UnitDivision extends UnitArea {
  areas: UnitArea[]
}

export interface UnitChart {
  by: 'unit'
  as_of: string | null
  divisions: UnitDivision[]
  areas_without_division: UnitArea[]
  unassigned: OrgPerson[]
  completeness: OrgCompleteness
}

export interface Division {
  id: string; code: string; name: string
  manager: string | null; manager_name: string | null
  is_active: boolean; areas_count: number
}

export interface Area {
  id: string; code: string; name: string
  division: string | null; division_name: string | null
  manager: string | null; manager_name: string | null
  is_active: boolean; employees_count: number
}

export interface AssignmentPeriod {
  id: string
  supervisor_id: string | null; supervisor_name: string | null
  area_id: string | null; area_name: string | null
  division_id: string | null; division_name: string | null
  valid_from: string; valid_until: string | null
  is_current: boolean
  reason: string
}

/** Lo que NO se manda, no se cambia: `undefined` conserva, `null` vacía. */
export interface AssignmentPayload {
  supervisor?: string | null
  area?: string | null
  division?: string | null
  effective_from?: string
  reason?: string
}

// ─── Organigrama ────────────────────────────────────────────────────────────

export function useOrgChart(params: { by: 'supervisor' | 'unit'; as_of?: string }) {
  return useQuery({
    queryKey: ['org-chart', params],
    queryFn: async () => {
      const r = await apiClient.get('/payroll/org-chart/', { params })
      return r.data as SupervisorChart | UnitChart
    },
  })
}

// ─── Divisiones y áreas ─────────────────────────────────────────────────────

export function useDivisions() {
  return useQuery({
    queryKey: ['org-divisions'],
    queryFn: async () => {
      const r = await apiClient.get('/payroll/divisions/')
      return r.data as { divisions: Division[]; count: number }
    },
  })
}

export function useAreas(divisionId?: string) {
  return useQuery({
    queryKey: ['org-areas', divisionId ?? null],
    queryFn: async () => {
      const r = await apiClient.get('/payroll/areas/', {
        params: divisionId ? { division_id: divisionId } : undefined,
      })
      return r.data as { areas: Area[]; count: number }
    },
  })
}

/** Invalida todo lo que cambia al tocar una unidad: el catálogo y el árbol. */
function useOrgInvalidation() {
  const qc = useQueryClient()
  return () => {
    qc.invalidateQueries({ queryKey: ['org-divisions'] })
    qc.invalidateQueries({ queryKey: ['org-areas'] })
    qc.invalidateQueries({ queryKey: ['org-chart'] })
  }
}

export function useSaveDivision() {
  const invalidate = useOrgInvalidation()
  return useMutation({
    mutationFn: async ({ id, ...data }: Partial<Division> & { id?: string }) => {
      const r = id
        ? await apiClient.patch(`/payroll/divisions/${id}/`, data)
        : await apiClient.post('/payroll/divisions/', data)
      return r.data as Division
    },
    onSuccess: invalidate,
  })
}

export function useDeleteDivision() {
  const invalidate = useOrgInvalidation()
  return useMutation({
    mutationFn: async (id: string) => { await apiClient.delete(`/payroll/divisions/${id}/`) },
    onSuccess: invalidate,
  })
}

export function useSaveArea() {
  const invalidate = useOrgInvalidation()
  return useMutation({
    mutationFn: async ({ id, ...data }: Partial<Area> & { id?: string }) => {
      const r = id
        ? await apiClient.patch(`/payroll/areas/${id}/`, data)
        : await apiClient.post('/payroll/areas/', data)
      return r.data as Area
    },
    onSuccess: invalidate,
  })
}

export function useDeleteArea() {
  const invalidate = useOrgInvalidation()
  return useMutation({
    mutationFn: async (id: string) => { await apiClient.delete(`/payroll/areas/${id}/`) },
    onSuccess: invalidate,
  })
}

// ─── Asignación de una persona ──────────────────────────────────────────────

export function useEmployeeAssignment(employeeId: string | null) {
  return useQuery({
    queryKey: ['employee-assignment', employeeId],
    queryFn: async () => {
      const r = await apiClient.get(`/payroll/employees/${employeeId}/assignment/`)
      return r.data as {
        employee: { id: string; name: string }
        current: AssignmentPeriod | null
        history: AssignmentPeriod[]
      }
    },
    enabled: !!employeeId,
  })
}

export function useAssignEmployee(employeeId: string) {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: async (payload: AssignmentPayload) => {
      const r = await apiClient.put(`/payroll/employees/${employeeId}/assignment/`, payload)
      return r.data
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['org-chart'] })
      qc.invalidateQueries({ queryKey: ['employee-assignment', employeeId] })
      // La jefatura vive denormalizada en la ficha: el listado de empleados la muestra.
      qc.invalidateQueries({ queryKey: ['employees'] })
    },
  })
}
